import { NodeId } from '@candulabs/craft';
import throttle from 'lodash/throttle';

import { setNodeLabelVisibility } from 'craft/components/NodeLabels/NodeLabel';
import { setResizeHandleVisibility } from 'craft/components/Resize/ResizeBoundariesObserver';
import { SetSize, Size, SizeType } from 'craft/components/Size';
import { CssSelectorId } from 'craft/types';
import { getAllDomNodesFromDocument, isDomElement } from 'craft/utils/viewport';

import {
  calculateNewSize,
  convertPixelsToSize,
  resetDomSizes,
  updateDomSizes,
} from './domManipulations';
import { Axes, DraggingStart, Movement } from './types';

export class ResizingAgent {
  nodeIds: NodeId[];

  doms: HTMLElement[];

  size: Size;

  setSize: SetSize;

  refreshCallback: () => void;

  draggingStart: DraggingStart | null;

  throttledOnMouseMove: (e: MouseEvent) => void;

  allowPercentages: boolean | undefined;

  cssSelectorId: CssSelectorId | undefined;

  constructor(
    nodeIds: NodeId[],
    refreshCallback: () => void,
    size: Size,
    setSize: SetSize,
    allowPercentages?: boolean,
    cssSelectorId?: CssSelectorId,
  ) {
    this.nodeIds = nodeIds;
    this.refreshCallback = refreshCallback;
    this.size = size;
    this.setSize = setSize;
    this.allowPercentages = allowPercentages;
    this.cssSelectorId = cssSelectorId;

    this.throttledOnMouseMove = throttle(this.onMouseMove, 50);
  }

  onMouseDown = (e: MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    this.doms = this.nodeIds
      .map((nodeId) => getAllDomNodesFromDocument(nodeId, undefined, this.cssSelectorId))
      .flat()
      .filter((dom) => dom !== null && dom instanceof HTMLElement) as HTMLElement[];
    // sanity check
    if (
      this.doms.length < 1 ||
      !isDomElement(this.doms[0]) ||
      !this.doms[0]?.offsetWidth ||
      !this.doms[0]?.offsetHeight
    ) {
      return;
    }

    this.draggingStart = {
      startingX: e.clientX,
      startingY: e.clientY,
      startingWidth: this.doms[0]?.offsetWidth,
      startingHeight: this.doms[0]?.offsetHeight,
      // @ts-expect-error
      axis: e.target?.getAttribute('data-axis'),
      // @ts-expect-error
      resizeHandle: e.target?.getAttribute('data-handle'),
      // @ts-expect-error
      movement: e.target?.getAttribute('data-movement') || Movement.Regular,
    };

    setResizeHandleVisibility(false);
    setNodeLabelVisibility(false);

    // @ts-expect-error
    const cursorAttribute = e.target?.getAttribute('data-cursor');
    document.body.style.cursor = cursorAttribute ? `${cursorAttribute}-resize` : 'auto';
    document.addEventListener('mousemove', this.throttledOnMouseMove, true);
    document.addEventListener('mouseup', this.onMouseUp, true);
  };

  onMouseMove = (e: MouseEvent) => {
    if (!this.draggingStart) {
      return;
    }
    // check if we're pressing the shift key. If so, we should maintain the aspect ratio.
    const shouldMaintainAspectRatio = e.shiftKey;
    const { clientX, clientY } = e;
    const { startingWidth, startingHeight, axis } = this.draggingStart;

    const { newWidth: mouseImpliedWidth, newHeight: mouseImpliedHeight } = calculateNewSize(
      { clientX, clientY },
      this.draggingStart,
    );

    let newHeight = mouseImpliedHeight;
    let newWidth = mouseImpliedWidth;
    if (shouldMaintainAspectRatio && axis === Axes.Y) {
      newWidth = Math.floor((1.0 * startingWidth * newHeight) / startingHeight);
    } else if (shouldMaintainAspectRatio && axis !== Axes.Y) {
      newHeight = Math.floor((1.0 * startingHeight * newWidth) / startingWidth);
    }

    const axisToUpdate = shouldMaintainAspectRatio ? Axes.All : axis;
    if (axisToUpdate === Axes.All) {
      this.draggingStart.latestWidth = newWidth;
      this.draggingStart.latestHeight = newHeight;
    } else if (axisToUpdate === Axes.Y) {
      this.draggingStart.latestHeight = newHeight;
    } else {
      this.draggingStart.latestWidth = newWidth;
    }
    this.doms.map((dom) => updateDomSizes(dom, axisToUpdate, newWidth, newHeight, this.size));

    this.draggingStart.shouldMaintainAspectRatio = shouldMaintainAspectRatio;

    // force a refresh to get the updated dom
    this.refreshCallback();
  };

  onMouseUp = (e: MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();

    this.unsubscribe();

    setResizeHandleVisibility(true);
    setNodeLabelVisibility(true);

    // persist
    this.updateComponentSize(
      this.draggingStart?.latestWidth,
      this.draggingStart?.latestHeight,
      this.doms[0]?.parentElement?.offsetWidth,
    );

    if (this.cssSelectorId) {
      this.doms.map((dom) => resetDomSizes(dom));
    }

    this.draggingStart = null;
    this.doms = [];
  };

  updateComponentSize = (latestWidth?: number, latestHeight?: number, parentWidth?: number) => {
    const { axis, shouldMaintainAspectRatio, startingWidth, startingHeight } =
      this.draggingStart || {};

    const axisToUpdate = shouldMaintainAspectRatio ? Axes.All : axis;

    const { width, height } = convertPixelsToSize(
      { width: latestWidth, height: latestHeight },
      { width: startingWidth, height: startingHeight },
      this.size,
      parentWidth,
      this.allowPercentages,
    );

    if (axisToUpdate === Axes.All && this.size.width.type !== SizeType.Percentage) {
      this.setSize({ ...this.size, width, height });
    } else if (axisToUpdate === Axes.X || axisToUpdate === Axes.All) {
      this.setSize({ ...this.size, width });
    } else if (axisToUpdate === Axes.Y && this.size.height.type !== SizeType.Percentage) {
      this.setSize({ ...this.size, height });
    }
  };

  unsubscribe = () => {
    document.body.style.cursor = 'auto';
    document.removeEventListener('mousemove', this.throttledOnMouseMove, true);
    document.removeEventListener('mouseup', this.onMouseUp, true);
  };

  tearDown = () => {
    this.unsubscribe();
    // TODO: use lodash's type definition to fix this
    // @ts-expect-error
    this.throttledOnMouseMove.cancel();
  };
}
